import { createUseStyles } from "react-jss";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { chooseLanguage } from "../utils/chooseLanguage";
import extractStrokesFromSVG, {
  InterpretationError,
} from "../utils/extractStrokesFromSVG";
import { getSymbol } from "../utils/getSymbol";
import RIImage from "../assets/ri.png";

interface Translation {
  letter: string;
  symbol: string;
}

export default function CharacterView({
  language,
  currentRoute,
  rows,
  isEnglishLetter,
  translationArray,
}: {
  language: any;
  currentRoute: string;
  rows: string[][];
  isEnglishLetter: boolean;
  translationArray: Translation[];
}) {
  const classes = useStyles();
  const location = useLocation();

  const renderCharacter = (character: any) => {
    if (!character) {
      return null;
    }
    const characterStatus = getCharacterStatus(
      character as keyof typeof language,
      currentRoute as string
    );

    return characterStatus === "missing" ? (
      <div className={`${classes.link} ${classes.disabledLink}`}>
        {character}
      </div>
    ) : (
      <NavLink
        key={character}
        to={`character/${encodeURIComponent(character)}`}
        className={({ isActive }) =>
          [
            classes.link,
            isActive ? classes.activeLink : "",
            characterStatus === "failing" ? classes.failingLink : "",
            " character-button",
          ].join(" ")
        }
        state={{
          currentRoute: currentRoute,
          character: character,
        }}
      >
        {currentRoute === "hiragana" &&
        character === "ri" &&
        !isEnglishLetter ? (
          <img
            src={RIImage}
            height={20}
            width={20}
            alt={
              isEnglishLetter
                ? character.replace(/_/g, " ")
                : getSymbol(character, translationArray)
            }
          />
        ) : isEnglishLetter ? (
          character.replace(/_/g, " ")
        ) : (
          getSymbol(character, translationArray)
        )}
      </NavLink>
    );
  };

  return (
    <div className={classes.wrapper}>
      <table className={classes.table}>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} className={classes.row} style={{ overflow: "hidden" }}>
              {row.map((cell, cellIndex) => (
                <td
                  style={{ overflow: "hidden" }}
                  key={cellIndex}
                  className={[
                    cellIndex,
                    location.state?.character === cell && cell !== ""
                      ? classes.activeLink
                      : "",
                  ].join(" ")}
                >
                  {renderCharacter(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <main style={{ marginTop: "20px" }}>
        <Outlet />
      </main>
    </div>
  );
}

type CharacterStatus = "working" | "failing" | "missing";

function getCharacterStatus(
  character: any,
  currentRoute: any
): CharacterStatus {
  const language = chooseLanguage(currentRoute);
  try {
    //@ts-ignore
    extractStrokesFromSVG(language[character]);
    return "working";
  } catch (error) {
    if (error instanceof InterpretationError && error.isNotDoneYet) {
      return "missing";
    } else {
      return "failing";
    }
  }
}

const useStyles = createUseStyles({
  row: {
    height: "40px",
  },
  cell: {
    border: "1px solid black",
    padding: "5px",
    overflow: "hidden",
  },
  wrapper: {
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  table: {
    "borderCollapse": "collapse",
    "overflow": "hidden",
    "& td": {
      border: "1px solid #ccc",
      padding: "8px",
      textAlign: "center",
      overflow: "scroll",
    },
    "& tr:first-child td": {
      background: "#ddd",
      fontWeight: "bold",
      overflow: "scroll",
    },
  },
  activeLink: {
    backgroundColor: "#ddd",
    borderColor: "lightgrey",
    color: "white",
  },
  disabledLink: {
    cursor: "default",
    opacity: 0.25,
    background: "#ddd",
    borderColor: "#ddd",
  },
  failingLink: {
    borderColor: "red",
    outline: "1px solid red",
  },
  link: {
    color: "black",
    textDecoration: "none",
  },
});
